import { useState } from "react";
import { UI_BASE } from "../config/bank";
import { ModalProvider, useModal } from "../context/ModalContext";
import { useAuth } from "../context/useAuth"

export default function SignupModal({ isOpen, onClose, onSuccess }) {
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { openModal } = useModal();
  const signup = useAuth((state) => state.signup);

  //if (!isOpen) return null;

  async function handleSignUp(e) {
    e.preventDefault();
    setError("");

    if (!username.trim() || !email.trim() || !password) {
      setError("Please fill in all fields.");
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setLoading(true);
    try {
      //const resp = await fetch(`${UI_BASE}/auth/signup`, {
      const resp = await fetch('/auth/signup', {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest" },
        body: JSON.stringify({ username, email, password }),
      });

      if (!resp.ok) {
        if (resp.status === 409) {
          setError("That username or email is already in use.");
        } else {
          setError("Unable to create account. Please try again.");
        }
        return;
      }

      const data = await resp.json();
      signup(username, data.token);
      onSuccess?.(data);
      onClose?.();
    } catch {
      setError("Unable to connect. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-[rgba(15,20,35,0.55)] p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="signup-modal-title"
    >
      <div className="w-full max-w-[380px] rounded-xl border border-black/10 bg-white p-8">

        {/* Header */}
        <div className="mb-7 flex items-center gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#EBF4FF] text-[#185FA5]">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
              aria-hidden="true">
              <circle cx="9" cy="8" r="4"/>
              <path d="M2 21c0-4 3.1-7 7-7s7 3 7 7"/>
              <path d="M18 8h4M20 6v4"/>
            </svg>
          </div>
          <div>
            <p id="signup-modal-title" className="m-0 text-[15px] font-medium text-gray-900">Create account</p>
            <p className="m-0 text-xs text-gray-400">Banking assistant</p>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="ml-auto p-1 text-base leading-none text-gray-400 hover:text-gray-600"
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSignUp} noValidate>

          {/* Username */}
          <div className="mb-4">
            <label htmlFor="signup-username" className="mb-1.5 block text-xs text-gray-500">Username</label>
            <div className="relative">
              <svg className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-300"
                width="16" height="16" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/>
              </svg>
              <input
                id="signup-username"
                type="text"
                value={username}
                onChange={e => setUsername(e.target.value)}
                placeholder="Choose a username"
                autoComplete="username"
                className="h-[38px] w-full rounded-lg border border-black/20 bg-white pl-[34px] pr-3 text-sm text-gray-900 outline-none focus:border-[#185FA5] focus:ring-1 focus:ring-[#185FA5]/30"
              />
            </div>
          </div>

          {/* Email */}
          <div className="mb-4">
            <label htmlFor="signup-email" className="mb-1.5 block text-xs text-gray-500">Email</label>
            <div className="relative">
              <svg className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-300"
                width="16" height="16" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <rect x="3" y="5" width="18" height="14" rx="2"/>
                <path d="m3 7 9 6 9-6"/>
              </svg>
              <input
                id="signup-email"
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="Enter your email"
                autoComplete="email"
                className="h-[38px] w-full rounded-lg border border-black/20 bg-white pl-[34px] pr-3 text-sm text-gray-900 outline-none focus:border-[#185FA5] focus:ring-1 focus:ring-[#185FA5]/30"
              />
            </div>
          </div>

          {/* Password */}
          <div className="mb-4">
            <label htmlFor="signup-password" className="mb-1.5 block text-xs text-gray-500">Password</label>
            <div className="relative">
              <svg className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-300"
                width="16" height="16" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <rect x="5" y="11" width="14" height="10" rx="2"/>
                <path d="M8 11V7a4 4 0 0 1 8 0v4"/>
              </svg>
              <input
                id="signup-password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="Create a password"
                autoComplete="new-password"
                className="h-[38px] w-full rounded-lg border border-black/20 bg-white pl-[34px] pr-10 text-sm text-gray-900 outline-none focus:border-[#185FA5] focus:ring-1 focus:ring-[#185FA5]/30"
              />
              <button
                type="button"
                onClick={() => setShowPassword(v => !v)}
                aria-label={showPassword ? "Hide password" : "Show password"}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-sm"
              >
                {showPassword ? "🙈" : "👁"}
              </button>
            </div>
          </div>

          {/* Confirm password */}
          <div className="mb-6">
            <label htmlFor="signup-confirm-password" className="mb-1.5 block text-xs text-gray-500">Confirm password</label>
            <div className="relative">
              <svg className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-300"
                width="16" height="16" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <rect x="5" y="11" width="14" height="10" rx="2"/>
                <path d="M8 11V7a4 4 0 0 1 8 0v4"/>
              </svg>
              <input
                id="signup-confirm-password"
                type={showPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                placeholder="Re-enter your password"
                autoComplete="new-password"
                className="h-[38px] w-full rounded-lg border border-black/20 bg-white pl-[34px] pr-3 text-sm text-gray-900 outline-none focus:border-[#185FA5] focus:ring-1 focus:ring-[#185FA5]/30"
              />
            </div>
          </div>

          {/* Error */}
          {error && (
            <div role="alert" className="mb-4 rounded-lg border border-red-300 bg-red-50 px-3 py-2.5 text-[13px] text-red-800">
              <span className="mr-1.5">⚠</span>{error}
            </div>
          )}

          {/* Submit */}
          <button
            type="submit"
            disabled={loading}
            className="h-10 w-full rounded-lg border border-blue-300 bg-[#EBF4FF] text-sm font-medium text-[#185FA5] transition-colors hover:bg-blue-100 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {loading ? "Creating account…" : "Create account"}
          </button>

        </form>

        {/* Switch to sign in */}
        <div className="mt-4 flex justify-center">
          <span className="text-[13px] text-gray-500">Already have an account?</span>
          <button
            type="button"
            onClick={()=>openModal("SIGNIN", {})}
            className="ml-1 text-[13px] font-medium text-[#185FA5] hover:underline"
          >
            Sign in
          </button>
        </div>

        {/* Footer */}
        <div className="mt-5 flex justify-center border-t border-black/[0.08] pt-4">
          <span className="text-[11px] text-gray-300">🔒 256-bit SSL encryption</span>
        </div>

      </div>
    </div>
  );
}
